import { RetryLink } from "@apollo/client/link/retry";
import { isAuthRelatedGraphqlMessage } from "Utils/graphqlAuthErrors";

/**
 * RetryLink handles retry logic for failed GraphQL operations.
 *
 * Link chain: RetryLink → ErrorLink → HttpLink
 *
 * This link comes FIRST in the chain, so it controls all retry behavior.
 * It decides whether to retry based on the error type.
 *
 * Retry strategy:
 * - ✅ Retry: Network failures (fetch errors), server errors (5xx)
 * - ❌ Don't retry: Auth errors (401), client errors (4xx)
 */
export const retryLink = new RetryLink({
    delay: {
        initial: 800,
        max: Infinity,
        jitter: false,
    },
    attempts: {
        max: 5,
        retryIf: (error) => {
            // Don't retry if there's no error
            if (!error) {
                return false;
            }

            // Don't retry on authentication errors (401)
            // These will be handled by ErrorLink which logs out the user
            if (error.statusCode === 401) {
                return false;
            }

            // Don't retry on auth-related error messages
            if (
                error.result &&
                error.result.errors &&
                error.result.errors.some(
                    (err: { message?: string }) =>
                        typeof err.message === "string" &&
                        isAuthRelatedGraphqlMessage(err.message),
                )
            ) {
                return false;
            }

            // Don't retry on other client errors (4xx)
            // These are usually validation errors, not found, etc.
            if (
                error.statusCode &&
                error.statusCode >= 400 &&
                error.statusCode < 500
            ) {
                return false;
            }

            // Retry on network errors (like "Failed to fetch")
            // These are usually temporary connectivity issues
            if (error.message?.includes("Failed to fetch")) {
                return true;
            }

            // Retry on server errors (5xx)
            // These might be temporary server issues
            if (error.statusCode && error.statusCode >= 500) {
                return true;
            }

            // For any other network errors without a status code, retry
            // This catches general network failures
            if (!error.statusCode) {
                return true;
            }
            // Default: don't retry
            return false;
        },
    },
});
